import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Link,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import WavingHandOutlinedIcon from "@mui/icons-material/WavingHandOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import TabletMacOutlinedIcon from "@mui/icons-material/TabletMacOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link as RouterLink } from "react-router-dom";

const headerIconFrameSx = {
  width: 72,
  height: 72,
  borderRadius: 2,
  overflow: "hidden",
  flexShrink: 0,
};

const headerTitleSx = {
  fontWeight: 700,
  fontSize: { xs: "2rem", md: "3rem" },
  lineHeight: 1,
};

const sectionTitleSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  fontWeight: 700,
};

const featureListItemSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1,
};

const AboutView = () => {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4 }}>
        <Stack spacing={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box sx={headerIconFrameSx}>
              <Box
                component="img"
                alt="SmartLog"
                src="assets/logos/SmartLog.png"
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
            <Box>
              <Typography variant="h3" sx={headerTitleSx}>
                About SmartLog
              </Typography>
              <Typography color="text.secondary">
                Ground investigation field logging and reporting for mobile teams.
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h6" sx={sectionTitleSx}>
              <WavingHandOutlinedIcon fontSize="small" />
              About Me and SmartLog
            </Typography>
            <Typography>
              Hi, I am Caleb, and I am the product manager of the Ground
              Investigation (GI) Mobile Application owned by the Geotechnical
              Engineering Office of the Civil Engineering and Development
              Department of the Hong Kong Government, now known as <strong>SmartLog</strong>.
              We have been researching and developing this application since
              April 2023 and are actively involving ground investigation
              specialists across the territory in the process.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" sx={sectionTitleSx}>
              <LightbulbOutlinedIcon fontSize="small" />
              Mission
            </Typography>
            <Typography>
              Our mission is to help Hong Kong-based ground investigation
              contractors get acquainted with digital workflows, and reduce
              dependence on proprietary software and libraries to process our
              ground data. Despite the availability of numerous GI logging
              systems in the market, we are taking this step to ensure that the
              GI Mobile App can be used by local practitioners and comply with
              GEOGuide 2 (Guide to Site Investigation), GEOGuide 3 (Guide to
              Soil and Rock Descriptions) and AGS4.0 data format (Guidelines on
              Digital Format).
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" sx={sectionTitleSx}>
              <TabletMacOutlinedIcon fontSize="small" />
              Platform Focus
            </Typography>
            <Typography>
              The application will be optimized for iOS on iPads, with
              alternative usage in Android supported tablets being developed
              concurrently.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={sectionTitleSx}>
              <BoltOutlinedIcon />
              Key features and functionality
            </Typography>
            <Stack component="ul" spacing={1} sx={{ pl: 3, m: 0 }}>
              <li style={featureListItemSx}>
                <CheckCircleOutlineOutlinedIcon fontSize="small" sx={{ mt: "2px" }} />
                <span>Support the input of GI field works data by different site staff on site.</span>
              </li>
              <li style={featureListItemSx}>
                <CheckCircleOutlineOutlinedIcon fontSize="small" sx={{ mt: "2px" }} />
                <span>
                  Able to store GI field works data in the device&apos;s memory and
                  sync updates when network connection is established.
                </span>
              </li>
              <li style={featureListItemSx}>
                <CheckCircleOutlineOutlinedIcon fontSize="small" sx={{ mt: "2px" }} />
                <span>Drawing function for trial pits and slope stripping.</span>
              </li>
              <li style={featureListItemSx}>
                <CheckCircleOutlineOutlinedIcon fontSize="small" sx={{ mt: "2px" }} />
                <span>Support uploading and storage of corebox photos.</span>
              </li>
              <li style={featureListItemSx}>
                <CheckCircleOutlineOutlinedIcon fontSize="small" sx={{ mt: "2px" }} />
                <span>
                  Provide location services based on the mobile phone&apos;s GNSS
                  capabilities.
                </span>
              </li>
              <li style={featureListItemSx}>
                <CheckCircleOutlineOutlinedIcon fontSize="small" sx={{ mt: "2px" }} />
                <span>
                  Generate logs and GI data in accordance with GEOGuides and
                  AGS formats that are shareable.
                </span>
              </li>
              <li style={featureListItemSx}>
                <CheckCircleOutlineOutlinedIcon fontSize="small" sx={{ mt: "2px" }} />
                <span>Role-based access controls for a variety of user types.</span>
              </li>
            </Stack>
          </Box>

          <Accordion elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" sx={sectionTitleSx}>
                Join us
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Typography>
                  We are still in the midst of development operations and are
                  releasing test versions periodically. During this time, you can
                  share feedback and help improve the application&apos;s development.
                </Typography>

                <Typography>
                  Your feedback will be greatly valued and will help us
                  understand user requirements. For any feedback items, please
                  include the following:
                </Typography>

                <Stack component="ul" spacing={1} sx={{ pl: 3, m: 0 }}>
                  <li>Comment or problematic behavior.</li>
                  <li>Expected behavior or suggestion.</li>
                  <li>
                    If it is a bug, kindly list the steps to replicate the bug or
                    crash behavior in detail. This will help us tremendously.
                  </li>
                  <li>Screenshot or image to illustrate the comment or suggestion.</li>
                </Stack>

                <Typography>
                  As the app will be optimized for pads and tablets in the
                  future, we encourage you to test it on devices with the same
                  aspect ratio and pixel size. Otherwise, mobile phones work too.
                </Typography>

                <Typography>
                  Please find enclosed links to the latest published versions for
                  Android and iOS respectively:
                </Typography>

                <Stack spacing={1} sx={{ pl: 1 }}>
                  <Typography>
                    Android: see the <Link component={RouterLink} to="/releases">Releases</Link> page.
                  </Typography>
                  <Typography>
                    iOS:{" "}
                    <Link
                      href="https://testflight.apple.com/join/sjn4EDm1"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      https://testflight.apple.com/join/sjn4EDm1
                    </Link>
                  </Typography>
                </Stack>

                <Typography>
                  Future releases will be posted when they are made available.
                </Typography>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </Paper>
    </Container>
  );
};

export default AboutView;
